import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

import ButtonLoader from "../Loaders/ButtonLoader";
import { formatDateAtTime2 } from "../../Utils/helper";
import UserChatBubble from "../Forms/FirmUserBtn";

import { getPdfThumbnail } from "./pdf-thumb";

// Keep in sync with DetailedViewCard. Files above this size never get a
// client-side thumbnail: the server ignores Range requests (answers 200),
// so page 1 costs a full download + parse.
const MAX_THUMB_MB = 20;

const sizeInMB = (s) => {
  if (typeof s === "number") return s / (1024 * 1024); // raw bytes
  if (!s) return 0;
  const n = parseFloat(s);
  if (Number.isNaN(n)) return 0;
  if (/gb/i.test(s)) return n * 1024;
  if (/kb/i.test(s)) return n / 1024;
  if (/mb/i.test(s)) return n;
  return n / (1024 * 1024); // numeric string, assume bytes
};

function mixColorWithWhite(hex, percentage) {
  const whitePercentage = (100 - percentage) / 100;

  let r = parseInt(hex?.slice(1, 3), 16);
  let g = parseInt(hex?.slice(3, 5), 16);
  let b = parseInt(hex?.slice(5, 7), 16);

  // Mix each channel with white
  r = Math.floor(r + (255 - r) * whitePercentage);
  g = Math.floor(g + (255 - g) * whitePercentage);
  b = Math.floor(b + (255 - b) * whitePercentage);

  return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
}

export const UpdatedViewCard = ({
  doc,
  slot,
  handleDocPreview,
  cardWidth = 255,
  rowIndex,
  cardPositionInRow,
  headerLabel,
  hideHeader = false,
}) => {
  const tooBig = !doc?.thumbnail && sizeInMB(doc?.file_size) > MAX_THUMB_MB;
  const [thumbUrl, setThumbUrl] = useState(doc?.thumbnail || null);
  const [thumbFailed, setThumbFailed] = useState(false);
  const containerRef = useRef(null);
  const cardWidthPx = `${cardWidth}px`;
  const imgWidthPx = `${cardWidth - 10}px`;
  const userOnlineStatus = useSelector((state) => state.chat2.userOnlineStatus);
  const pageSlotLabel =
    doc?.page?.id !== 19 && doc?.page_name !== "Litigation" && slot?.slot_number > 0 ? (
      <>
        <span className="text-primary-25">{slot.slot_number}.</span>{" "}
        {slot.slot_name?.trim() || doc?.file_name}
      </>
    ) : null;

  useEffect(() => {
    if (tooBig || thumbUrl || thumbFailed || !doc?.upload) return;
    const el = containerRef.current;
    if (!el) return;

    let cancelled = false;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        io.disconnect();
        getPdfThumbnail(doc.id, doc.upload, cardWidth - 10, 254)
          .then((url) => {
            if (!cancelled) setThumbUrl(url);
          })
          .catch(() => {
            if (!cancelled) setThumbFailed(true);
          });
      },
      { rootMargin: "200px" }
    );

    io.observe(el);
    return () => {
      cancelled = true;
      io.disconnect();
    };
  }, [tooBig, doc?.id, doc?.upload, thumbUrl, thumbFailed, cardWidth]);

  const getCardBackground = () => {
    const isEvenRow = rowIndex % 2 === 0;
    const isEvenPosition = cardPositionInRow % 2 === 0;

    if (isEvenRow) {
      return isEvenPosition ? "var(--primary-4)" : "var(--primary-2)";
    }
    return isEvenPosition ? "var(--primary-2)" : "var(--primary-4)";
  };

  return (
    <div
      className="d-flex flex-column align-items-center justify-content-center"
      style={{ marginRight: "0px" }}
    >
      {!hideHeader && !headerLabel && doc?.page_name === "Treatment" && (
        <div
          className="has-primary-font-detail-view-card"
          style={{
            display: "flex",
            justifyContent: doc?.panelName === null ? "center" : "flex-start",
            alignItems: "center",
            background: doc?.specialty?.color
              ? mixColorWithWhite(doc?.specialty?.color, 10)
              : "var(--primary-5)",
            color: "var(--primary)",
            width: cardWidthPx,
            minWidth: cardWidthPx,
            maxWidth: cardWidthPx,
            fontSize: "14px",
            fontWeight: "600",
            height: "25px",
            textTransform: "uppercase",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {doc?.panelName !== null && (
            <>
              <span
                className="d-flex m-r-5 align-items-center justify-content-center"
                style={{
                  width: "25px",
                  height: "25px",
                  background: doc?.specialty?.color || "#000",
                  color: "white",
                  minWidth: "25px",
                }}
              >
                {doc?.specialty?.name?.[0] || "?"}
              </span>
              <span
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  fontSize: "14px",
                  fontWeight: "600",
                  height: "25px",
                  textTransform: "uppercase",
                  alignContent: "center",
                }}
              >
                {pageSlotLabel || doc.panelName}
              </span>
            </>
          )}

          {doc?.panelName === null && (
            <>
              <span
                className="m-l-2"
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  fontSize: "14px",
                  fontWeight: "600",
                  height: "25px",
                  textTransform: "uppercase",
                  alignContent: "center",
                }}
              >
                {pageSlotLabel ||
                  (slot?.slot_number === 0
                    ? "Attached To Page"
                    : slot?.slot_name === null
                      ? `${slot?.slot_number}. ${doc?.file_name}`
                      : slot
                        ? `${slot?.slot_number}. ${slot?.slot_name}`
                        : "Attached To Page")}
              </span>
            </>
          )}
        </div>
      )}
      {!hideHeader && (headerLabel || doc?.page_name !== "Treatment") && (
        <div
          className="has-primary-font-detail-view-card"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "0px 10px",
            background: "var(--primary-15)",
            color: "var(--primary)",
            width: cardWidthPx,
            minWidth: cardWidthPx,
            maxWidth: cardWidthPx,
            fontSize: "14px",
            fontWeight: "600",
            height: "25px",
            textTransform: "uppercase",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          <div
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              flex: 1,
              minWidth: 0,
              textAlign: "center",
            }}
          >
            {headerLabel ||
              pageSlotLabel ||
              (doc?.page_name ? (
                doc.page_name === "Treatment" ? (
                  <>
                    <span>{doc?.specialty?.name?.[0]}</span> {doc?.panelName}
                  </>
                ) : doc?.panelName ? (
                  doc.panelName
                ) : slot?.slot_number === 0 ? (
                  "Attached To Page"
                ) : slot?.slot_name === null ? (
                  `${slot?.slot_number}. ${doc?.file_name}`
                ) : slot?.slot_name ? (
                  `${slot?.slot_number}. ${slot?.slot_name}`
                ) : (
                  "Attached To Page"
                )
              ) : (
                "Unsorted Document"
              ))}
          </div>
        </div>
      )}
      <div
        className={"font-weight-semibold cursor-pointer updated-empty-card p-0"}
        id="no-vertical-border"
        onClick={() => handleDocPreview(doc)}
        style={{
          width: cardWidthPx,
          minWidth: cardWidthPx,
          maxWidth: cardWidthPx,
          height: "369px",
          minHeight: "369px",
          border: "none",
          gap: "5px",
          background: getCardBackground(),
        }}
        key={doc.id}
      >
        <div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              height: "21px",
            }}
            className="p-l-5 p-r-5"
          >
            <p className="name new-view-title">
              {doc?.panelName &&
                `${slot?.slot_number}${slot?.slot_name ? ". " + slot?.slot_name : ""}`}
            </p>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              height: "21px",
            }}
            className="p-l-5 p-r-5"
          >
            <span
              className="d-flex align-items-center justify-content-center"
              style={{
                display: "inline-block",
                marginRight: "5px",
                width: "15px",
                height: "15px",
              }}
            >
              <i
                className="ic ic-15 ic-file-colored cursor-pointer"
                style={{
                  width: "15px",
                  height: "15px",
                }}
              />
            </span>
            <p title={doc.file_name || slot?.slot_name} className="name new-view-title">
              {doc.file_name || slot?.slot_name}
            </p>
          </div>
        </div>
        <div
          ref={containerRef}
          style={{
            height: "254px",
            maxHeight: "254px",
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
          }}
        >
          {thumbUrl ? (
            <img
              style={{
                height: "254px",
                width: imgWidthPx,
                objectFit: "contain",
              }}
              src={thumbUrl}
              alt=""
            />
          ) : tooBig || thumbFailed || !doc?.upload ? (
            <div className="d-flex flex-column align-items-center justify-content-center">
              <BrokenPreviewImage />
            </div>
          ) : (
            <ButtonLoader />
          )}
        </div>
        <div>
          <div className="updated-empty-card-sub-head first-sub-head mt-0 p-l-5 p-r-5">
            <div
              className="d-flex align-items-center"
              style={{
                gap: "5px",
              }}
            >
              <p
                className=""
                style={{
                  color: "var(--primary-25)",
                  fontWeight: "600",
                }}
              >
                Size
              </p>
              <p
                className=""
                style={{
                  fontWeight: "600",
                  textTransform: "lowercase",
                }}
              >
                {doc?.file_size}
              </p>
            </div>
            <div
              className="d-flex align-items-center"
              style={{
                gap: "5px",
              }}
            >
              <p
                className=""
                style={{
                  color: "var(--primary-25)",
                  fontWeight: "600",
                }}
              >
                Pages
              </p>
              <p
                style={{
                  fontWeight: "600",
                }}
              >
                {doc?.page_count}
              </p>
            </div>
          </div>
          <div className="updated-empty-card-sub-head p-l-5 p-r-5">
            <div
              className="d-flex align-items-center"
              style={{
                gap: "5px",
              }}
            >
              <p
                style={{
                  color: "var(--primary-25)",
                  fontWeight: "600",
                }}
              >
                Uploaded
              </p>
              <p
                style={{
                  fontWeight: "600",
                }}
              >
                {formatDateAtTime2(doc?.created)}
              </p>
            </div>
          </div>
          <div className="updated-empty-card-sub-head w-100">
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                height: "21px",
                alignItems: "center",
              }}
              className="w-100"
            >
              <UserChatBubble
                userId={doc?.attached_by?.id}
                first_name={doc?.attached_by?.first_name || ""}
                last_name={doc?.attached_by?.last_name || ""}
                profilePic={doc?.attached_by?.profile_pic_29p ?? ""}
                is_active={doc?.attached_by?.is_active}
                width={19}
                height={25}
                isActive={userOnlineStatus?.[doc?.attached_by?.id]?.isOnline ?? false}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const UpdatedEmptyCard = ({
  slot,
  page,
  item,
  isDragging,
  refetchLoading,
  handleDrop,
  handleUploadPopup,
  isSlotUploading,
  style,
}) => {
  return (
    <div
      onDrop={(e) => handleDrop(e, slot.id, page?.page?.id, item?.id)}
      className={`dropzone-${slot.id}-${item?.id}-${page.page.id} font-weight-semibold cursor-pointer updated-empty-card`}
      id="no-vertical-border"
      onClick={() => handleUploadPopup(slot?.id, item?.id, page?.page?.id)}
      style={{ ...style }}
    >
      {isSlotUploading(slot?.id, item?.id) && (isDragging || refetchLoading) ? (
        <div className="d-flex align-items-center justify-content-center">
          <ButtonLoader />
          <span style={{ marginLeft: "5px" }}>Uploading</span>
        </div>
      ) : (
        <div
          style={{ height: "100%" }}
          className="d-flex align-items-center justify-content-center"
        >
          <p
            className="name"
            style={{
              fontWeight: 600,
              paddingBottom: "14px",
              color: "var(--primary-25)",
              textAlign: "center",
            }}
          >
            {slot?.slot_name ? `Add ${slot?.slot_name} +` : "Add File +"}
          </p>
        </div>
      )}
    </div>
  );
};

export const UpdatedUploadCard = ({
  page,
  item,
  isDragginguploadingbutton,
  uploadbuttonLoadingid,
  handleDropwithOutpanlesWithoutslots,
  handleUploadPopupwithoutSlots,
  style,
}) => {
  return (
    <div
      className="col-12 col-md-6 col-xl-3 icon-text-box text-center order-last font-weight-semibold cursor-pointer updated-empty-card updated-mb-5"
      onDrop={(event) => handleDropwithOutpanlesWithoutslots(event, true, page?.page?.id, item?.id)}
      onClick={() => handleUploadPopupwithoutSlots(page?.page?.id)}
      style={{ marginRight: "5px", ...style }}
    >
      <div className="upload-icon border-0 rounded-0 bg-transparent">
        <div className="d-flex align-items-center width-inherit justify-content-center">
          {isDragginguploadingbutton && item?.id === uploadbuttonLoadingid ? (
            <>
              <ButtonLoader />
              <span style={{ marginLeft: "5px" }}>Uploading..</span>
            </>
          ) : (
            <p
              className="updated-empty-card-sub-head"
              style={{ fontWeight: 600, color: "var(--primary-25)" }}
            >
              Upload Document to Page{" "}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export const UpdatedUploadCardWithoutPanelsWithoutSlots = ({
  page,
  isDragginguploadingbutton,
  handleDropwithOutpanlesWithoutslots,
  handleUploadPopupwithoutSlots,
}) => {
  return (
    <div
      className="text-center order-last font-weight-semibold cursor-pointer updated-empty-card updated-mb-5"
      onDrop={(event) => handleDropwithOutpanlesWithoutslots(event, false, page?.page?.id, 0)}
      onClick={() => handleUploadPopupwithoutSlots(page?.page?.id)}
      style={{ marginRight: "5px" }}
    >
      <div className="upload-icon border-0 rounded-0 bg-transparent">
        <div className="d-flex align-items-center width-inherit justify-content-center">
          {isDragginguploadingbutton ? (
            <>
              <ButtonLoader />
              <span style={{ marginLeft: "5px" }}>Uploading..</span>
            </>
          ) : (
            <p
              className="detailed-empty-card-sub-head"
              style={{ fontWeight: 600, color: "var(--primary-25)" }}
            >
              Upload Document to Page{" "}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export const UpdatedUploadCardNoLoader = ({
  page,
  item,
  handleDropwithOutpanlesWithoutslots,
  handleUploadPopupwithoutSlots,
}) => {
  return (
    <div
      className="col-12 col-md-6 col-xl-3 icon-text-box text-center order-last font-weight-semibold cursor-pointer updated-empty-card updated-mb-5"
      onDrop={(event) => handleDropwithOutpanlesWithoutslots(event, true, page?.page?.id, item?.id)}
      onClick={() => handleUploadPopupwithoutSlots(page?.page?.id)}
      style={{ marginRight: "5px" }}
    >
      <div className="upload-icon border-0 rounded-0 bg-transparent">
        <div className="d-flex align-items-center width-inherit justify-content-center">
          <p
            className="detailed-empty-card-sub-head"
            style={{ fontWeight: 600, color: "var(--primary-25)" }}
          >
            Upload Document to Page{" "}
          </p>
        </div>
      </div>
    </div>
  );
};

const BrokenPreviewImage = () => {
  return (
    <svg height={24} width={24} viewBox="0 0 64 64" enableBackground="new 0 0 64 64" fill="#000000">
      <g strokeWidth="0" />
      <g strokeLinecap="round" strokeLinejoin="round" />
      <g>
        <g>
          <polygon
            fill="none"
            stroke="#000000"
            strokeWidth="2"
            strokeMiterlimit="10"
            points="23,1 55,1 55,63 9,63 9,15 "
          />
          <polyline
            fill="none"
            stroke="#000000"
            strokeWidth="2"
            strokeMiterlimit="10"
            points="9,15 23,15 23,1 "
          />
        </g>
        <circle
          fill="none"
          stroke="#000000"
          strokeWidth="2"
          strokeMiterlimit="10"
          cx="32"
          cy="36"
          r="9"
        />
        <line
          fill="none"
          stroke="#000000"
          strokeWidth="2"
          strokeMiterlimit="10"
          x1="25.834"
          y1="29.834"
          x2="38.166"
          y2="42.166"
        />
      </g>
    </svg>
  );
};
